import { useCallback, useEffect, useState } from 'react';

import { normalizeFavorites, toggleFavorite } from './epg.js';

/**
 * 즐겨찾는 채널 (v25, `docs/51` §8 · 절대 규칙 69).
 *
 * ★ **`audio-muted.ts`·`ads/ads-enabled.ts`·`offers/offers-enabled.ts` 셋과 같은 모양이다.**
 * 저장소에 이미 세 번 반복된 「기기 로컬 선호」 패턴이고, 넷째로 그 자리에 선다 —
 * 키 상수 + try/catch 읽기·쓰기 + 같은 문서 `CustomEvent` + 다른 탭 `storage` + 훅.
 *
 * ★★ **서버에도 계정에도 저장하지 않는다.** 계정 축에 두면 `requireLogin=false` 배포에서
 * **기능이 통째로 사라지는데**, 데모가 그렇게 도는 경우가 많다. 절대 규칙 46 이 계정 축을
 * **이어보기 하나로** 좁혀 둔 것과 같은 방침이다.
 *
 * ★★★ **`bloom.channelId` 와 합치지 않는다.** `channels.ts` 가 `last-context.ts` 의
 * `channelId` 에 대해 적어 둔 그 경고와 같은 종류다 — 합치면 **별을 누르는 것이 다음
 * 방문 채널을 바꾼다.**
 *
 * **판정은 여기 없다.** 정렬·소속·정화는 `epg.ts` 의 순수 함수이고, 이 파일은 저장소를
 * 만지는 얇은 껍데기다 — `localStorage` 를 직접 만지는 자리에 판정을 두면 그 규칙이
 * **node 테스트에서 통째로 빠진다**(`channel-selection.ts` 가 순수로 떨어져 나온 그 이유).
 */
const STORAGE_KEY = 'bloom.favoriteChannels';

/** 같은 문서 안의 다른 컴포넌트에 알린다. `storage` 이벤트는 **다른 탭에만** 온다 */
const CHANGE_EVENT = 'bloom:favorite-channels';

/**
 * 저장된 즐겨찾기. **읽을 때마다 모양을 다시 검증한다** — `last-context.ts` 의
 * `loadLastContext()` 와 같은 방침이다: 저장한 것은 우리지만 그 사이에 계약이 바뀌었을 수
 * 있고, **모양이 어긋난 것을 그대로 화면에 넘기면 `.includes` 가 던져 편성표가 통째로
 * 안 그려진다.** 증상은 「그 기기에서만 채널 화면이 빈다」 하나이고 **새 프로필로는
 * 재현되지 않는다.**
 */
export function readFavoriteChannels(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return normalizeFavorites(raw === null ? null : JSON.parse(raw));
  } catch {
    // 저장소가 막혔거나(사파리 프라이빗) JSON 이 망가졌다. 즐겨찾기가 없는 것으로 본다.
    return [];
  }
}

export function writeFavoriteChannels(next: readonly string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeFavorites(next)));
  } catch {
    // 저장하지 못해도 이 탭에서는 아래 이벤트로 바뀐다. 새로고침하면 돌아간다.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * 화면이 이 값을 그린다. **재생 경로는 이것을 구독하지 않는다** — 즐겨찾기는 목록의
 * 순서일 뿐이고 **재생을 한 픽셀도 바꾸지 않는다.** 그것이 「← 를 끝까지 누르면 별에
 * 선다」를 감당할 수 있는 이유다 (`docs/51` §8.3).
 */
export function useFavoriteChannels(): [readonly string[], (channelId: string) => void] {
  const [favorites, setFavorites] = useState<readonly string[]>(readFavoriteChannels);

  useEffect(() => {
    const sync = () => {
      setFavorites(readFavoriteChannels());
    };
    window.addEventListener(CHANGE_EVENT, sync);
    // 다른 탭에서 바꾼 경우.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const toggle = useCallback((channelId: string) => {
    const next = toggleFavorite(readFavoriteChannels(), channelId);
    writeFavoriteChannels(next);
    setFavorites(next);
  }, []);

  return [favorites, toggle];
}
